import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppShell } from "../components/AppShell";
import { useAuth } from "../lib/auth";
import { api, formatApiError } from "../lib/api";
import { LOGO_URL } from "../lib/constants";
import {
    LogOut,
    Target,
    Calculator,
    TrafficCone,
    Crown,
    Clock,
    XCircle,
    ChevronRight,
    Building2,
    Save,
    AtSign,
    Phone,
    Hash,
    MapPin,
    User as UserIcon,
} from "lucide-react";
import { toast } from "sonner";

export default function Profile() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const initial = (user?.name || "R").charAt(0).toUpperCase();

    return (
        <AppShell>
            <div className="max-w-xl mx-auto text-center animate-fade-up">
                <div className="inline-flex items-center justify-center">
                    <div
                        className="h-24 w-24 rounded-full flex items-center justify-center font-display text-5xl shadow-card"
                        style={{ background: "#1a2a44", color: "#c9a961" }}
                        data-testid="profile-avatar"
                    >
                        {initial}
                    </div>
                </div>
                <h1 className="mt-5 font-display text-4xl sm:text-5xl text-brand-ink leading-tight" data-testid="profile-name">
                    {user?.name}
                </h1>
                {user?.restaurant_name && (
                    <div className="mt-1 eyebrow text-brand-gold">{user.restaurant_name}</div>
                )}
                <div className="mt-2 text-sm text-brand-muted" data-testid="profile-email">{user?.email}</div>
            </div>

            <div className="max-w-2xl mx-auto mt-10 space-y-4">
                <div className="eyebrow text-brand-ink/60">Abbonamento</div>
                <SubscriptionCard user={user} onClick={() => navigate("/billing")} />
            </div>

            <div className="max-w-2xl mx-auto mt-10">
                <div className="eyebrow text-brand-ink/60 mb-3">Dati ristorante (intestazione documenti)</div>
                <RestaurantForm />
            </div>

            <div className="max-w-2xl mx-auto mt-8 space-y-4">
                <div className="eyebrow text-brand-ink/60">Informazioni</div>
                <InfoCard
                    icon={<Target size={18} />}
                    title="Food Cost target consigliato"
                    value="28 – 32%"
                    note="Valori superiori al 35% compromettono la marginalità."
                />
                <InfoCard
                    icon={<Calculator size={18} />}
                    title="Formula prezzo suggerito"
                    value="Costo porzione / 0.30"
                    note="Garantisce un food cost del 30% sul prezzo di vendita."
                />
                <InfoCard
                    icon={<TrafficCone size={18} />}
                    title="Soglie Food Cost"
                    value={
                        <span className="inline-flex items-center gap-4">
                            <span className="text-brand-success">&lt; 30% Verde</span>
                            <span className="text-brand-warning">&lt; 35% Giallo</span>
                            <span className="text-brand-danger">≥ 35% Rosso</span>
                        </span>
                    }
                    note="Il sistema colora i badge in base a queste soglie."
                />
            </div>

            <div className="max-w-2xl mx-auto mt-10 flex justify-center">
                <button
                    onClick={() => { logout(); navigate("/login"); }}
                    data-testid="profile-logout"
                    className="inline-flex items-center gap-2 rounded-full border border-brand-danger/40 text-brand-danger px-6 py-2.5 text-sm font-semibold hover:bg-brand-danger/5 transition"
                >
                    <LogOut size={14} /> Esci
                </button>
            </div>

            <div className="mt-12 flex flex-col items-center justify-center gap-3 pb-4">
                <img src={LOGO_URL} alt="RENOVA360" className="h-10 w-10 object-contain opacity-70" />
                <div className="eyebrow text-brand-muted text-center">
                    Rengineering & Normalization of Value
                </div>
            </div>
        </AppShell>
    );
}

function RestaurantForm() {
    const { user, refresh } = useAuth();
    const [form, setForm] = useState({
        name: "",
        restaurant_name: "",
        restaurant_address: "",
        restaurant_city: "",
        vat_number: "",
        phone: "",
        contact_email: "",
    });
    const [saving, setSaving] = useState(false);
    const [dirty, setDirty] = useState(false);

    useEffect(() => {
        if (user) {
            setForm({
                name: user.name || "",
                restaurant_name: user.restaurant_name || "",
                restaurant_address: user.restaurant_address || "",
                restaurant_city: user.restaurant_city || "",
                vat_number: user.vat_number || "",
                phone: user.phone || "",
                contact_email: user.contact_email || "",
            });
            setDirty(false);
        }
    }, [user]);

    const set = (key) => (e) => {
        setForm((f) => ({ ...f, [key]: e.target.value }));
        setDirty(true);
    };

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await api.put("/auth/profile", form);
            await refresh();
            setDirty(false);
            toast.success("Dati ristorante salvati");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    return (
        <form
            onSubmit={submit}
            className="bg-paper rounded-xl border border-brand-line shadow-card p-6 space-y-4"
            data-testid="restaurant-form"
        >
            <div className="flex items-center gap-3 pb-3 border-b border-brand-line">
                <div className="h-10 w-10 rounded-full bg-brand-navy text-brand-gold flex items-center justify-center">
                    <Building2 size={18} />
                </div>
                <div className="flex-1">
                    <div className="font-display text-lg text-brand-ink leading-tight">
                        Intestazione documenti
                    </div>
                    <div className="text-[12px] text-brand-muted">
                        Verranno stampati su ordini fornitore, esportazioni PDF e ricevute.
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field
                    icon={<UserIcon size={14} />}
                    label="Nome titolare"
                    value={form.name}
                    onChange={set("name")}
                    placeholder="Mario Rossi"
                    testid="rf-name"
                />
                <Field
                    icon={<Building2 size={14} />}
                    label="Nome ristorante"
                    value={form.restaurant_name}
                    onChange={set("restaurant_name")}
                    placeholder="Trattoria da Mario"
                    testid="rf-restaurant-name"
                />
                <Field
                    icon={<MapPin size={14} />}
                    label="Indirizzo"
                    value={form.restaurant_address}
                    onChange={set("restaurant_address")}
                    placeholder="Via Roma 12"
                    testid="rf-address"
                    className="sm:col-span-2"
                />
                <Field
                    icon={<MapPin size={14} />}
                    label="Città / CAP"
                    value={form.restaurant_city}
                    onChange={set("restaurant_city")}
                    placeholder="20100 Milano (MI)"
                    testid="rf-city"
                />
                <Field
                    icon={<Hash size={14} />}
                    label="Partita IVA / C.F."
                    value={form.vat_number}
                    onChange={set("vat_number")}
                    placeholder="12345678901"
                    testid="rf-vat"
                />
                <Field
                    icon={<Phone size={14} />}
                    label="Telefono"
                    value={form.phone}
                    onChange={set("phone")}
                    placeholder="+39 02 1234567"
                    testid="rf-phone"
                />
                <Field
                    icon={<AtSign size={14} />}
                    label="Email di contatto"
                    value={form.contact_email}
                    onChange={set("contact_email")}
                    placeholder="ordini@trattoria.it"
                    testid="rf-contact-email"
                    type="email"
                />
            </div>

            <div className="flex items-center justify-between pt-2">
                <div className="text-[11px] text-brand-muted italic">
                    {dirty ? "Modifiche non salvate" : "Tutti i dati sono salvati"}
                </div>
                <button
                    type="submit"
                    disabled={saving || !dirty}
                    data-testid="rf-save"
                    className="inline-flex items-center gap-2 rounded-full bg-brand-navy text-white px-5 py-2 text-sm font-semibold hover:bg-brand-navyLight disabled:opacity-60 transition"
                >
                    <Save size={14} /> {saving ? "Salvataggio…" : "Salva dati"}
                </button>
            </div>
        </form>
    );
}

function Field({ icon, label, value, onChange, placeholder, testid, className = "", type = "text" }) {
    return (
        <label className={`block ${className}`}>
            <span className="block text-[11px] uppercase tracking-[0.16em] text-brand-muted font-semibold mb-1 inline-flex items-center gap-1.5">
                {icon}
                {label}
            </span>
            <input
                type={type}
                value={value}
                onChange={onChange}
                placeholder={placeholder}
                data-testid={testid}
                className="w-full px-3 py-2 rounded-lg bg-white border border-brand-line text-sm focus:outline-none focus:border-brand-gold focus:ring-2 focus:ring-brand-gold/15 transition"
            />
        </label>
    );
}

function InfoCard({ icon, title, value, note }) {
    return (
        <div className="bg-paper rounded-xl border border-brand-line shadow-card p-5 flex items-start gap-4">
            <div className="h-10 w-10 rounded-full bg-brand-navy text-brand-gold flex items-center justify-center shrink-0">
                {icon}
            </div>
            <div className="flex-1">
                <div className="text-[11px] uppercase tracking-wider text-brand-muted font-semibold">
                    {title}
                </div>
                <div className="mt-1 font-display text-xl text-brand-ink">{value}</div>
                {note && <div className="mt-1 text-[12px] text-brand-muted italic font-display">{note}</div>}
            </div>
        </div>
    );
}

function SubscriptionCard({ user, onClick }) {
    const status = user?.subscription_status || "trialing";
    const days = user?.days_left ?? 0;
    const isActive = status === "active";
    const isTrial = status === "trialing";

    const cfg = isActive
        ? { tint: "#2e7d32", icon: <Crown size={18} />, title: "Abbonato", sub: "Rinnovi in automatico ogni 30 giorni", cta: "Gestisci" }
        : isTrial
            ? { tint: "#c9a961", icon: <Clock size={18} />, title: `Prova gratuita — ${days} gg rimasti`, sub: "Poi 29€/mese. Abbonati ora per non perdere i tuoi dati.", cta: "Abbonati" }
            : { tint: "#d32f2f", icon: <XCircle size={18} />, title: "Prova scaduta", sub: "Riattiva l'abbonamento per continuare.", cta: "Riattiva" };

    return (
        <button
            onClick={onClick}
            data-testid="profile-subscription-card"
            className="w-full text-left bg-paper rounded-xl border border-brand-line shadow-card hover:shadow-cardHover hover:border-brand-gold/50 transition-all duration-300 p-5 flex items-start gap-4 group"
            style={{ borderLeft: `4px solid ${cfg.tint}` }}
        >
            <div
                className="h-10 w-10 rounded-full flex items-center justify-center shrink-0"
                style={{ background: `${cfg.tint}1a`, color: cfg.tint }}
            >
                {cfg.icon}
            </div>
            <div className="flex-1">
                <div className="font-display text-lg text-brand-ink leading-tight">{cfg.title}</div>
                <div className="mt-0.5 text-[12px] text-brand-muted">{cfg.sub}</div>
            </div>
            <div className="flex items-center gap-2 text-sm font-semibold" style={{ color: cfg.tint }}>
                {cfg.cta}
                <ChevronRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
            </div>
        </button>
    );
}
